import { tabs as lluiTabs, type TabsMsg, type TabsState } from '@llui/components/tabs';
import { button, component, div, island, span, text, type Mountable } from '@llui/dom';

/**
 * `<Tabs>` — a thin DungeonLogs composition over LLui's standard tabs state
 * machine and connected part bags.
 *
 * The host still owns the active route/panel. The local LLui island owns
 * keyboard navigation, roving focus, disabled-item handling, and the active
 * trigger contract; it reports value changes through `onActivate`.
 */

export interface TabsItem {
  value: string;
  label: string;
  /** True when this is the currently-active tab on mount. */
  active: boolean;
  /** Optional trailing count badge. `0` remains visible. */
  count?: number;
  /** Disabled tabs remain visible and are skipped by keyboard navigation. */
  disabled?: boolean;
  /** Optional id for `aria-controls`. Defaults to `{idBase}-{value}-panel`. */
  panelId?: string;
}

export interface TabsOptions {
  /** Called with the tab value whenever LLui activates a different tab. */
  onActivate: (value: string) => void;
  /** Tab items in display order. */
  items: TabsItem[];
  /** Id prefix used for tab + panel ids; defaults to `dl-tabs`. */
  idBase?: string;
  /** Optional CSS class for app-specific layout extension. */
  className?: string;
  /** Forwarded `data-testid` on the tab root. */
  testid?: string;
  /** ARIA label for the tablist. */
  ariaLabel: string;
}

interface TabsEffect {
  type: 'activate';
  value: string;
}

function initialValue(items: TabsItem[]): string {
  return (
    items.find((item) => item.active && item.disabled !== true)?.value ??
    items.find((item) => item.disabled !== true)?.value ??
    ''
  );
}

function pageDirection(): 'ltr' | 'rtl' {
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
}

export function tabs(opts: TabsOptions): Mountable {
  const idBase = opts.idBase ?? 'dl-tabs';
  const values = opts.items.map((item) => item.value);
  const disabledItems = opts.items.filter((item) => item.disabled === true).map((item) => item.value);
  const initialState = lluiTabs.init({
    value: initialValue(opts.items),
    items: values,
    disabledItems,
    activation: 'automatic',
    dir: pageDirection(),
  });

  const def = component<TabsState, TabsMsg, TabsEffect>({
    name: `Tabs(${idBase})`,
    init: () => [initialState, []],
    update: (state, msg) => {
      const [next] = lluiTabs.update(state, msg);
      const effects: TabsEffect[] =
        next.value === state.value ? [] : [{ type: 'activate', value: next.value }];
      return [next, effects];
    },
    view: ({ state, send }) => {
      const parts = lluiTabs.connect(state, send, { id: idBase });
      const klass = `dl-tabs${opts.className ? ` ${opts.className}` : ''}`;

      return [
        div(
          {
            ...parts.root,
            class: klass,
            'data-testid': opts.testid,
          },
          [
            div(
              {
                ...parts.list,
                class: 'dl-tabs-list',
                'aria-label': opts.ariaLabel,
              },
              opts.items.map((item) => {
                const standard = parts.item(item.value).trigger;
                const tabId = `${idBase}-${item.value}-tab`;
                const panelId = item.panelId ?? `${idBase}-${item.value}-panel`;

                return button(
                  {
                    ...standard,
                    id: tabId,
                    'aria-controls': panelId,
                    class: 'dl-tabs-trigger',
                    disabled: item.disabled === true,
                  },
                  item.count === undefined
                    ? [text(item.label)]
                    : [text(item.label), span([text(` ${String(item.count)}`)])],
                );
              }),
            ),
          ],
        ),
      ];
    },
    onEffect: (effect) => opts.onActivate(effect.value),
  });

  return div({ class: 'dl-tabs-boundary' }, [
    island({
      def,
      initialState,
      reason: 'The standard tabs machine owns local roving focus while the host owns routing.',
    }),
  ]);
}
